import React, { ChangeEvent, Component } from 'react';
import { RootState } from '../../../../../redux/store';
import { ConfigurationState } from '../../../../../redux/reducers/ConfigurationReducer';
import { changeConfiguration } from '../../../../../redux/actions/ConfigurationActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { CaseGraphField } from '../../../../../domain/case/CaseDomains';
import FormicaInput from '../../../../custom/FormicaInput';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../../custom/FormicaSearchSelectBox';
import { AnimatedButton } from '@trlogic/ui-pack';
import CustomTable from '../../../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import CustomTableContentWrapper from '../../../../custom/CustomTableContentWrapper';
import Icon from 'icons/Icon';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../../config/binding/InstanceBinding';
import Injectables from '../../../../../constant/Injectables';
import CollectionNames from '../../../../../constant/CollectionNames';
import { TransactionJsonModel } from '../../../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../../../custom/CustomLoading';
import FormicaToggleSwitch from '../../../../custom/FormicaToggleSwitch';
import MessageUtil from '../../../../../util/MessageUtil';
import RoleHelper from '../../../../../RoleHelper';
import DataType from 'constant/DataType';
import EmptyComponent from '../../../../custom/empty-components/EmptyComponent';
import ComponentName from '../../../../../constant/ComponentName';
import eq = query.eq;
import LanguageUtil from '../../../../../util/LanguageUtil';

class DomainGraphContainer extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      configuration: { configuration },
    } = this.props;
    const { graphField, loading } = this.state;
    const disabled = this.isDisabled();

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'flex flex-col w-full py-5 gap-y-3 px-5 gap-x-3'}>
        <div className={'flex gap-x-3 w-full items-center'}>
          <div className={'flex w-full gap-x-3'}>
            <FormicaSearchSelectBox
              rounded
              onChange={this.onChangeGraphField}
              value={graphField?.field}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.variable', defaultMessage: 'Variable' })}
              options={this.getTransactionVariables()}
              disabled={disabled}
              data-testid={`${ComponentName.CONFIGURATION}_graph_creator_variable_select`}
            />
            <FormicaInput
              onChange={this.onChangeGraphFieldDescription}
              value={graphField?.description}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
              disabled={disabled}
              data-testid={`${ComponentName.CONFIGURATION}_graph_creator_description_input`}
            />
          </div>
          <div className={'flex w-1/3 gap-x-3'}>
            <FormicaSearchSelectBox
              rounded
              onChange={this.onChangeColor}
              value={graphField?.color}
              iconNode={graphField.color && <div style={{ backgroundColor: graphField.color }} className={'h-4 w-4 shrink-0 rounded-full'}></div>}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.color', defaultMessage: 'Color' })}
              disabled={disabled}
              options={this.getColors()}
              data-testid={`${ComponentName.CONFIGURATION}_graph_creator_color_select`}
            />
          </div>
          <div className={'flex w-[200px]'}>
            <AnimatedButton
              onClick={this.addGraphField}
              className={'w-full'}
              customSize={'sm'}
              icon={'add'}
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
              customType={'primary'}
              disabled={disabled}
              data-testid={`${ComponentName.CONFIGURATION}_graph_creator_add_button`}
            />
          </div>
        </div>
        {configuration.graphConfiguration != undefined && configuration.graphConfiguration.length != 0 ? (
          <CustomTable columns={this.getColumns()} value={configuration.graphConfiguration} />
        ) : (
          <EmptyComponent type={ComponentName.CONFIGURATION} />
        )}
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private tracker = async () => {
    const {
      configuration: { configuration },
    } = this.props;
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', configuration.dataSource)).build();
      const transaction: TransactionJsonModel | undefined = await this.repository.find(transactionQuery);
      this.setState({ transaction, loading: false });
    }
  };

  private startTracker = async () => {
    const {
      configuration: { configuration },
    } = this.props;
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', configuration.dataSource));
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionVariables = (): FormicaSelectBoxItem[] => {
    const { transaction } = this.state;
    if (!transaction) {
      return [];
    }
    return transaction.variables
      .filter((v) => v.properties.dataType == DataType.STRING)
      .map((variable) => ({
        label: variable.description || variable.name,
        value: variable.name,
        dataType: variable.properties.dataType,
      }));
  };

  private onChangeGraphField = (item: FormicaSelectBoxItem) => {
    const { graphField } = this.state;
    if (this.isDisabled()) return;
    graphField.field = item.value;
    graphField.label = item.label;
    graphField.dataType = item.dataType;
    this.setState({ graphField });
  };

  private onChangeGraphFieldDescription = (event: ChangeEvent<HTMLInputElement>) => {
    const { graphField } = this.state;
    if (this.isDisabled()) return;
    graphField.description = event.target.value;
    this.setState({ graphField });
  };

  private onChangeColor = (item: FormicaSelectBoxItem) => {
    const { graphField } = this.state;
    if (this.isDisabled()) return;
    graphField.color = item.value;
    this.setState({ graphField });
  };

  private onChangeGraphFieldColor = (graphField: CaseGraphField, item: FormicaSelectBoxItem) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (this.isDisabled()) return;
    const index = configuration.graphConfiguration.findIndex((f) => f.field == graphField.field);
    if (index == -1) return;
    configuration.graphConfiguration[index].color = item.value;
    changeConfiguration(configuration);
  };

  private addGraphField = () => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    const { graphField } = this.state;
    if (this.isDisabled()) return;
    if (!graphField.field || !graphField.color) return;
    if (configuration.graphConfiguration.find((f) => f.field == graphField.field)) {
      MessageUtil.error({
        content: LanguageUtil.get({ id: 'messages.error.variableAlready', defaultMessage: 'This variable is already added.' }),
      });
      return;
    }
    configuration.graphConfiguration.push(graphField);
    changeConfiguration(configuration);
    const newGraphField: CaseGraphField = {
      field: '',
      description: '',
      label: '',
      icon: '',
      enabled: false,
      color: '',
      dataType: DataType.STRING,
    };
    this.setState({ graphField: newGraphField });
  };

  private removeGraphField = (index: number) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (this.isDisabled()) return;
    configuration.graphConfiguration.splice(index, 1);
    changeConfiguration(configuration);
  };

  private onChangeGraphFieldEnabled = (record: CaseGraphField) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (this.isDisabled()) return;
    const index = configuration.graphConfiguration.findIndex((graphField) => graphField.field == record.field);
    if (index == -1) return;
    configuration.graphConfiguration[index].enabled = !record.enabled;
    changeConfiguration(configuration);
  };

  private getColors = (): FormicaSelectBoxItem[] => {
    return [
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.limeGreen', defaultMessage: 'Lime Green' }),
        value: '#84CC16',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#84CC16' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.darkGreen', defaultMessage: 'Dark Green' }),
        value: '#027A48',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#027A48' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.dodgerBlue', defaultMessage: 'Dodger Blue' }),
        value: '#0EA5E9',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#0EA5E9' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.turquoise', defaultMessage: 'Turquoise' }),
        value: '#14B8A6',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#14B8A6' }}></div>,
      },
      {
        label: 'Royal Blue',
        value: '#175CD3',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#175CD3' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.turquoiseBlue', defaultMessage: 'Turquoise Blue' }),
        value: '#06B6D4',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#06B6D4' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.gray', defaultMessage: 'Gray' }),
        value: '#4F4F4F',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#4F4F4F' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.purple', defaultMessage: 'Purple' }),
        value: '#5925DC',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#5925DC' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.periwinkleBlue', defaultMessage: 'Periwinkle Blue' }),
        value: '#6366F1',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#6366F1' }}></div>,
      },
      {
        label: LanguageUtil.get({ id: 'pages.configuration.graph.colors.yellow', defaultMessage: 'Yellow' }),
        value: '#FACC15',
        iconNode: <div className={'h-4 w-4 rounded-full'} style={{ backgroundColor: '#FACC15' }}></div>,
      },
    ];
  };

  private getColumns = (): ColumnProps[] => {
    const disabled = this.isDisabled();
    return [
      {
        field: 'field',
        header: LanguageUtil.get({ id: 'pages.configuration.graph.table.variable', defaultMessage: 'Variable' }),
        alignHeader: 'left',
        className: 'min-w-[250px] max-w-[300px]',
        body: (record: CaseGraphField) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                clipSize={30}
                showEllipsis={true}
                text={record.label}
                data-testid={`${ComponentName.CONFIGURATION}_graph_${record.field}_variable_text`}
              />
            </div>
          );
        },
      },
      {
        field: 'description',
        className: 'min-w-[300px] max-w-[350px]',
        header: LanguageUtil.get({ id: 'pages.configuration.graph.table.description', defaultMessage: 'Description' }),
        body: (record: CaseGraphField) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                clipSize={38}
                showEllipsis={true}
                text={
                  record.description ||
                  LanguageUtil.get({ id: 'pages.configuration.graph.table.noDescription', defaultMessage: 'This field have not description yet.' })
                }
                data-testid={`${ComponentName.CONFIGURATION}_graph_${record.field}_description_text`}
              />
            </div>
          );
        },
      },
      {
        field: 'color',
        header: LanguageUtil.get({ id: 'pages.configuration.graph.table.color', defaultMessage: 'Color' }),
        className: 'min-w-[300px] max-w-[300px]',
        body: (record: CaseGraphField) => {
          return (
            <div className={'flex items-center justify-center w-5/6'}>
              <FormicaSearchSelectBox
                options={this.getColors()}
                onChange={(item) => this.onChangeGraphFieldColor(record, item)}
                ghost
                disabled={disabled}
                iconNode={<div style={{ backgroundColor: record.color }} className={'h-4 w-4 rounded-full'}></div>}
                value={record.color}
                rounded
                data-testid={`${ComponentName.CONFIGURATION}_graph_${record.field}_color_select`}
              />
            </div>
          );
        },
      },
      {
        field: 'enabled',
        header: LanguageUtil.get({ id: 'pages.configuration.graph.table.enabled', defaultMessage: 'Default' }),
        className: 'min-w-[10%]',
        body: (record: CaseGraphField) => {
          return (
            <div className={'text-xs 2xl:text-md flex w-full items-center justify-start px-3'}>
              <FormicaToggleSwitch
                value={record.enabled || false}
                onChange={(e) => this.onChangeGraphFieldEnabled(record)}
                disabled={disabled}
                data-testid={`${ComponentName.CONFIGURATION}_graph_${record.field}_enabled_switch`}
              />
            </div>
          );
        },
      },
      {
        field: 'actions',
        header: LanguageUtil.get({ id: 'pages.configuration.graph.table.actions', defaultMessage: 'Actions' }),
        className: 'min-w-[10%]',
        body: (record: CaseGraphField) => {
          const {
            configuration: { configuration },
          } = this.props;
          const index = configuration.graphConfiguration.findIndex((graphField) => graphField.field === record.field);
          return (
            <div
              onClick={() => {
                this.removeGraphField(index);
              }}
              className={'text-xs 2xl:text-md flex justify-center items-center'}
              data-testid={`${ComponentName.CONFIGURATION}_graph_${record.field}_remove_button`}
            >
              <Icon icon={'delete'} size={20} className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} text-f-black-3`} />
            </div>
          );
        },
      },
    ];
  };

  private isDisabled = () => {
    return !RoleHelper.canEdit('CaseConfiguration');
  };
}

interface Props extends ReduxProps {}

class State {
  graphField: CaseGraphField = {
    field: '',
    description: '',
    label: '',
    icon: '',
    enabled: false,
  };

  transaction: TransactionJsonModel | undefined = undefined;
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  configuration: state.configuration as ConfigurationState,
});
const mapDispatchToProps = {
  changeConfiguration,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DomainGraphContainer);
